import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import { apiGet, apiPost, apiPut } from '../utils/apiUtils';
import { prepareAssetFile } from '../utils/assetConversion';
import './AssetsCategory.css';

const BULK_UPLOAD_CONCURRENCY = 4;

const MusicPlayer = ({ asset, audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (audioRef.current) {
      const progressBar = e.currentTarget;
      const clickPosition = e.nativeEvent.offsetX;
      const progressBarWidth = progressBar.offsetWidth;
      const newTime = (clickPosition / progressBarWidth) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  return (
    <div className="ac-music-player">
      <div className="ac-music-icon">🎵</div>
      <audio
        ref={audioRef}
        preload="none"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      >
        <source src={audioUrl} />
      </audio>
      <div className="ac-audio-controls">
        <button className="ac-play-btn" onClick={togglePlay}>
          {isPlaying ? '⏸️' : '▶️'}
        </button>
        <div className="ac-progress-container" onClick={handleProgressClick}>
          <div
            className="ac-progress-fill"
            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
        <div className="ac-time">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
    </div>
  );
};

const AssetsCategory = ({ novelId, novel, refreshNovel, isProduction = false }) => {
  const [selectedAssetType, setSelectedAssetType] = useState('sprite');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('Загрузка...');
  const [editingAsset, setEditingAsset] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkInputRef = useRef(null);

  // Use assets from novel prop instead of local state
  const assets = novel?.assets || { sprites: [], backgrounds: [], musics: [] };

  const assetTypes = [
    { key: 'sprite', label: 'Спрайты', icon: '🎭' },
    { key: 'background', label: 'Фоны', icon: '🏞️' },
    { key: 'music', label: 'Музыка', icon: '🎵' }
  ];

  // Prevent body scroll when modal is open and calculate modal position
  useEffect(() => {
    if (editingAsset) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [editingAsset]);

  /* Many files at once: the name comes from the file name, the description stays empty. Converting and
     the direct-to-GCS uploads run in parallel, but registering is one confirm at a time because the
     backend rewrites the whole novel on each confirm and parallel confirms would overwrite each other. */
  const handleBulkUpload = async (event) => {
    const files = Array.from(event.target.files || [])
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    event.target.value = '';
    if (files.length === 0) return;

    const assetType = selectedAssetType;
    const getUploadUrl = isProduction ? API_URLS.PRODUCTION_ASSETS_UPLOAD_URL : API_URLS.BUILDING_ASSETS_UPLOAD_URL;
    const getConfirmUrl = isProduction ? API_URLS.PRODUCTION_ASSETS_CONFIRM : API_URLS.BUILDING_ASSETS_CONFIRM;
    const failures = [];
    const uploaded = new Array(files.length).fill(null);
    let cursor = 0;
    let done = 0;

    setBulkLoading(true);
    setUploadError(`Загрузка файлов: 0 из ${files.length}`);

    const worker = async () => {
      while (cursor < files.length) {
        const index = cursor++;
        const file = files[index];
        try {
          const uploadFile = await prepareAssetFile(file, assetType);
          const urlResp = await apiGet(
            getUploadUrl(novelId),
            { params: { filename: uploadFile.name, asset_type: assetType } },
            false, '', true
          );
          const { upload_url, asset_id, extension, content_type } = urlResp.data;
          const gcsResp = await fetch(upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': content_type },
            body: uploadFile,
          });
          if (!gcsResp.ok) throw new Error(`GCS upload failed: ${gcsResp.status}`);
          uploaded[index] = { asset_id, extension, name: file.name.replace(/\.[^.]+$/, '').trim() || file.name };
        } catch (err) {
          failures.push(`${file.name}: ${err.response?.data?.detail || err.message}`);
        }
        done += 1;
        setUploadError(`Загрузка файлов: ${done} из ${files.length}`);
      }
    };

    try {
      await Promise.all(Array.from({ length: Math.min(BULK_UPLOAD_CONCURRENCY, files.length) }, worker));

      const ready = uploaded.filter(Boolean);
      let saved = 0;
      for (const item of ready) {
        try {
          await apiPost(
            getConfirmUrl(novelId),
            { asset_id: item.asset_id, name: item.name, prompt_description: '', asset_type: assetType, extension: item.extension },
            {}, false, '', true
          );
          saved += 1;
        } catch (err) {
          failures.push(`${item.name}: ${err.response?.data?.detail || err.message}`);
        }
        setUploadError(`Сохранение: ${saved} из ${ready.length}`);
      }

      await refreshNovel();
      const shownFailures = failures.slice(0, 5).join('\n');
      const more = failures.length > 5 ? `\n…и ещё ${failures.length - 5}` : '';
      setUploadError(
        failures.length === 0
          ? `Загружено ресурсов: ${saved}`
          : `Загружено ${saved} из ${files.length}. Не загружены:\n${shownFailures}${more}`
      );
    } finally {
      setBulkLoading(false);
    }
  };

  const handleEditAsset = async (event) => {
    event.preventDefault();
    setUploadError(null);
    setUploadLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name');
    const description = formData.get('description');
    const file = formData.get('file');

    try {
      let newExtension = null;
      let oldExtension = null;
      if (file && file.size > 0) {
        // Step 0: convert to WebP / MP3 in the browser so only the compressed file is uploaded
        setUploadStatus('Конвертация...');
        const uploadFile = await prepareAssetFile(file, selectedAssetType);
        setUploadStatus('Загрузка...');

        // Step 1: get a signed URL for the replacement file
        const uploadUrlResp = await apiGet(
          (isProduction ? API_URLS.PRODUCTION_ASSET_UPLOAD_URL : API_URLS.BUILDING_ASSET_UPLOAD_URL)(novelId, editingAsset.id),
          { params: { filename: uploadFile.name } }
        );
        const { upload_url, content_type, extension, old_extension } = uploadUrlResp.data;
        newExtension = extension;
        oldExtension = old_extension;

        // Step 2: PUT directly to GCS using fetch — must NOT include auth headers
        // Use the exact content_type the signed URL was created with
        const gcsResp = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': content_type },
          body: uploadFile,
        });
        if (!gcsResp.ok) throw new Error(`GCS upload failed: ${gcsResp.status}`);
      }

      // Step 3: update name / description / extension via the backend
      const updateData = {};
      if (name && name.trim()) updateData.name = name.trim();
      if (description && description.trim()) updateData.prompt_description = description.trim();
      if (newExtension) {
        updateData.extension = newExtension;
        if (oldExtension) updateData.old_extension = oldExtension;
      }

      if (Object.keys(updateData).length > 0) {
        await apiPut(
          isProduction ? API_URLS.PRODUCTION_ASSET(novelId, editingAsset.id) : API_URLS.BUILDING_ASSET(novelId, editingAsset.id),
          updateData
        );
      }

      setEditingAsset(null);
      setUploadError('Ресурс обновлен успешно!');
      await refreshNovel();
    } catch (err) {
      setUploadError(err.response?.data?.detail || err.message || 'Ошибка обновления');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот ресурс?')) {
      return;
    }

    try {
      await axios.delete(
        isProduction ? API_URLS.PRODUCTION_ASSET(novelId, assetId) : API_URLS.BUILDING_ASSET(novelId, assetId)
      );
      setUploadError('Ресурс удален успешно!');

      // Refresh novel data
      await refreshNovel();
    } catch (err) {
      setUploadError(err.response?.data?.detail || err.message);
    }
  };

  const getCurrentAssets = () => {
    const assetKeyMap = {
      'sprite': 'sprites',
      'background': 'backgrounds',
      'music': 'musics'
    };
    const key = assetKeyMap[selectedAssetType];
    return assets[key] || [];
  };

  const getAssetUrl = (asset) => {
    const basePath = isProduction ? 'production' : 'building';
    const url = `${GCS_BUCKET_URL}/${basePath}/${novelId}/${selectedAssetType}/${asset.id}.${asset.extension}`;
    return asset.cache_id ? `${url}?cache_id=${asset.cache_id}` : url;
  };

  const getAddButtonText = () => {
    switch (selectedAssetType) {
      case 'sprite':
        return 'Добавить спрайты';
      case 'background':
        return 'Добавить фоны';
      case 'music':
        return 'Добавить музыку';
      default:
        return 'Добавить ресурсы';
    }
  };

  return (
    <div className="ac-wrap">
      {/* Success/Error messages */}
      {uploadError && (
        <div className="ac-error" style={{ whiteSpace: 'pre-line' }}>
          {uploadError}
        </div>
      )}

      {/* Edit Form */}
      {editingAsset && (
        <div className="ac-overlay" onClick={() => setEditingAsset(null)}>
          <div className="ac-modal" onClick={e => e.stopPropagation()}>
            <h3 className="ac-modal-title">Редактировать {editingAsset.name}</h3>
            {uploadError && <div className="ac-error">{uploadError}</div>}
            <form onSubmit={handleEditAsset}>
              <div className="ac-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название ресурса"
                  defaultValue={editingAsset.name}
                  disabled={uploadLoading}
                  className="ac-input"
                />
              </div>
              <div className="ac-form-group">
                <textarea
                  name="description"
                  placeholder="Описание для промпта"
                  defaultValue={editingAsset.prompt_description}
                  disabled={uploadLoading}
                  className="ac-textarea"
                  rows={3}
                />
              </div>
              <div className="ac-form-group">
                <input
                  type="file"
                  name="file"
                  disabled={uploadLoading}
                  className="ac-file-input"
                  accept={selectedAssetType === 'music' ? 'audio/*' : 'image/*'}
                />
              </div>
              <div className="ac-modal-actions">
                <button
                  type="submit"
                  disabled={uploadLoading}
                  className="ac-btn-submit"
                >
                  {uploadLoading ? uploadStatus : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingAsset(null)}
                  disabled={uploadLoading}
                  className="ac-btn-cancel"
                >
                  Отменить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Asset Type Tabs */}
      <div className="ac-tabs">
        {assetTypes.map((type) => (
          <button
            key={type.key}
            className={`ac-tab ${selectedAssetType === type.key ? 'active' : ''}`}
            onClick={() => setSelectedAssetType(type.key)}
          >
            <span className="tab-icon">{type.icon}</span>
            <span className="tab-label">{type.label}</span>
            <span className="tab-count">({(assets[type.key + 's'] || []).length})</span>
          </button>
        ))}
      </div>

      {/* Add assets: one or many files, name = file name, empty description */}
      <input
        ref={bulkInputRef}
        type="file"
        multiple
        hidden
        accept={selectedAssetType === 'music' ? 'audio/*' : 'image/*'}
        onChange={handleBulkUpload}
      />
      <button
        className="ac-add-btn"
        onClick={() => { setUploadError(null); bulkInputRef.current.click(); }}
        disabled={bulkLoading}
      >
        <span>➕</span>
        {bulkLoading ? 'Загрузка…' : getAddButtonText()}
      </button>

      {/* Assets List */}
      {getCurrentAssets().length > 0 ? (
        <div className="ac-grid">
          {getCurrentAssets().map((asset) => (
            <div key={asset.id} className="ac-card">
              <div className="ac-card-preview">
                {selectedAssetType === 'music' ? (
                  <MusicPlayer asset={asset} audioUrl={getAssetUrl(asset)} />
                ) : (
                  <img
                    src={getAssetUrl(asset)}
                    alt={asset.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                )}
                <div className="asset-placeholder" style={{display: 'none'}}>
                  {selectedAssetType === 'sprite' ? '🎭' :
                   selectedAssetType === 'background' ? '🏞️' : '🎵'}
                </div>
              </div>
              <div className="ac-card-info">
                <h4 className="ac-card-name">{asset.name}</h4>
                <p className="ac-card-desc">{asset.prompt_description}</p>
                <div className="ac-card-actions">
                  <button
                    className="ac-btn-edit"
                    onClick={() => { setUploadError(null); setEditingAsset(asset); }}
                  >
                    Редактировать
                  </button>
                  <button
                    className="ac-btn-delete"
                    onClick={() => handleDeleteAsset(asset.id)}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ac-empty">
          <p>Ресурсов пока нет. Загрузите свой первый ресурс!</p>
        </div>
      )}
    </div>
  );
};

export default AssetsCategory;
